/**
 * 饼图组件 - 用于 MetricCard 展示分类分布
 * 文档: src/pages/opinion/specs/003-ads-overview/components/MetricCard/MetricPieChart.md
 */
import React, { useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { chartAllColors } from '@op/constants';
import { DBO_V } from '@op/utils/format';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import type { TRequestPieBackType } from '@op/utils/requestUtil';
import './index.scss';
import useLanguage from '@op/hooks/useLanguage';

/**
 * 根据翻译映射表获取翻译后的文本
 * @param text - 需要翻译的文本
 * @param translationMap - 翻译映射表
 * @returns 翻译后的文本，如果没有映射则返回原文本
 */
const getTranslatedText = (
  text: string,
  translationMap?: Record<string, string>,
) => translationMap?.[text] ?? text;

interface IMetricPieChartProps {
  data?: TRequestPieBackType;
  translationMap?: Record<string, string>; // 翻译映射
  height?: number;
  colors?: string[];
  radius?: [string, string];
  loading?: boolean;
  title: string;
}

const MetricPieChart: React.FC<IMetricPieChartProps> = ({
  data,
  translationMap,
  height = 120,
  colors,
  radius = ['50%', '80%'],
  loading,
  title
}) => {
  const { formatMessage } = useLanguage();
  // ==================== 饼图配置 ====================
  const { chartOption, legendData } = useMemo(() => {
    if (!data?.series?.length || data?.isEmpty)
      return { chartOption: null, legendData: [] };

    const chartColors = colors || chartAllColors;
    const total = data.series.reduce((sum, item) => sum + (item.value || 0), 0);

    const pieData = data.series.map((item, index) => ({
      name: getTranslatedText(item.name, translationMap), // 应用翻译
      value: item.value,
      itemStyle: {
        color: chartColors[index % chartColors.length],
      },
    }));

    // 计算图例数据（名称、颜色、百分比）- 使用翻译后的名称
    const legendItems = data.series.map((item, index) => ({
      name: getTranslatedText(item.name, translationMap), // 应用翻译
      color: chartColors[index % chartColors.length],
      value: item.value,
      percent: total > 0 ? ((item.value / total) * 100).toFixed(1) : '0.0',
    }));

    return {
      chartOption: {
        tooltip: {
          // show: false,
          trigger: 'item',
          confine: true,
          formatter: (params: any) =>
            formatChartParamsDate({
              params: [params],
              title: formatMessage(title),
              valueFormat: (v: number) =>
                `${DBO_V.formatThousand(v)} (${params.percent}%)`,
              listTitleFunc: (item) => getTranslatedText(item.name, translationMap),
            }),
        },
        // 图例（默认不显示，空间有限）
        legend: {
          show: false, // 使用自定义图例
        },
        grid: {
          left: 0,
          right: 20,
          top: 0,
          bottom: 0,
          containLabel: false,
        },
        xAxis: {
          show: false,
        },
        yAxis: {
          show: false,
        },
        series: [
          {
            type: 'pie',
            radius,
            center: ['35%', '50%'], // 靠左显示，给右侧图例留空间
            avoidLabelOverlap: false,
            label: {
              show: false,
            },
            emphasis: {
              label: {
                show: false,
              },
              scale: true,
              scaleSize: 5,
              borderRadius: 2,
              borderColor: '#fff',
              borderWidth: 1
            },
            data: pieData,
            itemStyle: {
              borderRadius: 2,
              borderColor: '#fff',
              borderWidth: 1
            }
          },
        ],
      },
      legendData: legendItems,
    };
  }, [data, colors, radius, translationMap]);

  // ==================== 渲染逻辑 ====================
  return (
    <div className="dbo-metric-pie-chart">
      <EChartCommon
        options={chartOption}
        height={height}
        hasEmptyImg={false}
        empty={!data?.series?.length || data?.isEmpty}
        loading={loading}
      />
      {legendData.length > 0 && (
        <div className="dbo-metric-pie-chart__legend">
          {legendData.map((item, index) => (
            <div key={index} className="dbo-metric-pie-chart__legend-item">
              <span
                className="dbo-metric-pie-chart__legend-dot"
                style={{ backgroundColor: item.color }}
              />
              <span className="dbo-metric-pie-chart__legend-name">
                {getTranslatedText(item.name, translationMap)}
              </span>
              <span className="dbo-metric-pie-chart__legend-percent">
                {item.percent}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(MetricPieChart);
